import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { Shield, Flame, Trophy, Plus, LogOut, Crown, Sparkles, ArrowDownAZ, Eye, EyeOff, Zap, Clock, User, Swords } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BattleCard from "@/components/BattleCard";
import BossBattleCard from "@/components/BossBattleCard";
import DeployShieldDialog from "@/components/DeployShieldDialog";
import AddSubscriptionDialog from "@/components/AddSubscriptionDialog";
import PremiumModal from "@/components/PremiumModal";
import ConnectBankButton from "@/components/ConnectBankButton";
import HeroInsightBanner from "@/components/HeroInsightBanner";
import AllClearCard from "@/components/AllClearCard";
import VictoryModal from "@/components/VictoryModal";
import HeroMascot from "@/components/HeroMascot";
import HeroCoach from "@/components/HeroCoach";
import AppMenu from "@/components/AppMenu";
import AIHeroPanel from "@/components/AIHeroPanel";
import NextBestAction from "@/components/NextBestAction";
import CommandCenterStrip from "@/components/CommandCenterStrip";
import HeroScoreShield from "@/components/HeroScoreShield";
import DailyChallengeCard from "@/components/DailyChallengeCard";
import RecentWinCard from "@/components/RecentWinCard";
import InstallPrompt from "@/components/InstallPrompt";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const FREE_DEFEAT_LIMIT = 2;

const LEVEL_THRESHOLDS = [
  { score: 100, label: "MAX HERO", desc: "Perfect score! Dash bows to your discipline.", colors: ["#FBBF24", "#F59E0B", "#FFFFFF", "#0F172A"] },
  { score: 95,  label: "LEGENDARY",      desc: "95+ Hero Score — true master of your money.", colors: ["#FBBF24", "#F59E0B", "#FFFFFF"] },
  { score: 90,  label: "CHAMPION TIER",  desc: "Hero Score crossed 90 — Dash levels up!",     colors: ["#FBBF24", "#F59E0B", "#10B981"] },
];

function fireLevelUpConfetti(colors) {
  const base = { ticks: 160, gravity: 0.85, decay: 0.93, startVelocity: 55, scalar: 1.1 };
  confetti({ ...base, particleCount: 160, spread: 110, origin: { y: 0.5 }, colors });
  confetti({ ...base, particleCount: 80, angle: 60,  spread: 70, origin: { x: 0, y: 0.7 }, colors });
  confetti({ ...base, particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors });
}

function fireGoldSparkleBurst(originY = 0.35) {
  // Subtle, short celebratory burst — gold/cream palette, small particles
  const gold = ["#FBBF24", "#F59E0B", "#FDE68A", "#FFFFFF"];
  const base = { ticks: 90, gravity: 0.7, decay: 0.92, startVelocity: 30, scalar: 0.75, shapes: ["circle"] };
  confetti({ ...base, particleCount: 45, spread: 80, origin: { y: originY }, colors: gold });
  window.setTimeout(() => {
    confetti({ ...base, particleCount: 28, angle: 60, spread: 55, origin: { x: 0.15, y: originY + 0.1 }, colors: gold });
    confetti({ ...base, particleCount: 28, angle: 120, spread: 55, origin: { x: 0.85, y: originY + 0.1 }, colors: gold });
  }, 120);
}

function computeLevelUp(prevScore, nextScore) {
  for (const t of LEVEL_THRESHOLDS) {
    if (nextScore >= t.score && prevScore < t.score) return t;
  }
  return null;
}

/**
 * firstName — pull just the first name from a full name string.
 */
function firstName(full = "") {
  const s = String(full || "").trim();
  if (!s) return "Hero";
  return s.split(/\s+/)[0];
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [subs, setSubs] = useState([]);
  const [hero, setHero] = useState({ score: 0, active_battles: 0, monthly_total: 0, defeated_count: 0 });
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [streak, setStreak] = useState({ weeks: 0, checked_in_this_week: false, boss_weeks: 0 });
  const [boss, setBoss] = useState({ sub: null, defeated_this_week: false, week_id: "" });
  const [loading, setLoading] = useState(true);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [premiumMsg, setPremiumMsg] = useState("");
  const [plaidStatus, setPlaidStatus] = useState({ configured: false, linked: false });
  const [insights, setInsights] = useState([]);
  const [dismissedKinds, setDismissedKinds] = useState(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem("mmh_dismissed_insight_kinds");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const [sortBy, setSortBy] = useState(() => {
    if (typeof window === "undefined") return "threat";
    try {
      return window.localStorage.getItem("mmh_sort_by") || "threat";
    } catch {
      return "threat";
    }
  });
  const [hideDefeated, setHideDefeated] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      const raw = window.localStorage.getItem("mmh_hide_defeated");
      return raw === null ? true : raw === "true";
    } catch {
      return true;
    }
  });
  const lastScore = useRef(null);
  const allClearCelebrated = useRef(false);
  const [showAllClear, setShowAllClear] = useState(false);
  const [vault, setVault] = useState({ balance: 0, total_diverted: 0, deposit_count: 0, recent_transactions: [] });
  const [vaultDelta, setVaultDelta] = useState(null);
  const [victorySub, setVictorySub] = useState(null);
  const [victoryBusy, setVictoryBusy] = useState(false);
  const [heroLevel, setHeroLevel] = useState({ level: 1, label: "Recruit", xp: 0 });

  const isFree = ((user && user !== false && user.tier) || "recruit") === "recruit";
  const defeatsRemaining = Math.max(0, FREE_DEFEAT_LIMIT - (hero.defeated_count || 0));
  const atDefeatLimit = isFree && defeatsRemaining <= 0;

  const loadAll = useCallback(async () => {
    try {
      const [subsRes, heroRes, streakRes, bossRes, plaidRes, auditRes, vaultRes, levelRes] = await Promise.all([
        api.get("/subscriptions"),
        api.get("/hero-score"),
        api.post("/checkin"),
        api.get("/boss-battle"),
        api.get("/plaid/status").catch(() => ({ data: { configured: false, linked: false } })),
        api.post("/audit/start").catch(() => ({ data: { insights: [] } })),
        api.get("/vault").catch(() => ({ data: { balance: 0, total_diverted: 0, deposit_count: 0, recent_transactions: [] } })),
        api.get("/daily/hero-level").catch(() => ({ data: { level: 1, label: "Recruit", xp: 0 } })),
      ]);
      setSubs(subsRes.data);
      setHero(heroRes.data);
      setStreak(streakRes.data);
      setBoss(bossRes.data);
      setPlaidStatus(plaidRes.data);
      setInsights(auditRes.data?.insights || []);
      setVault(vaultRes.data);
      setHeroLevel(levelRes.data);
      setHeroLoaded(true);
      lastScore.current = heroRes.data.score;
    } catch (e) {
      console.error("Failed to load dashboard", e);
      toast.error("Couldn't load your battles. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Hamburger menu shortcuts: when AppMenu fires custom events we click the
  // existing buttons rather than duplicate their logic.
  useEffect(() => {
    const onCoach = () => document.querySelector('[data-testid="coach-launcher"]')?.click();
    const onConnect = () => document.querySelector('[data-testid="plaid-connect-button"]')?.click();
    window.addEventListener("mmh:open-coach", onCoach);
    window.addEventListener("mmh:connect-bank", onConnect);
    return () => {
      window.removeEventListener("mmh:open-coach", onCoach);
      window.removeEventListener("mmh:connect-bank", onConnect);
    };
  }, []);

  // Trigger level-up celebrations when score crosses thresholds
  useEffect(() => {
    if (lastScore.current === null) return;
    const before = lastScore.current;
    const after = hero.score;
    if (after > before) {
      const milestone = computeLevelUp(before, after);
      if (milestone) {
        fireLevelUpConfetti(milestone.colors);
        toast.success(`${milestone.label} unlocked!`, { description: milestone.desc, duration: 5000 });
      }
    }
    lastScore.current = after;
  }, [hero.score]);

  // "All clear" celebration — once per session when every insight is handled
  // (either defeated or dismissed). Requires the user to actually have had
  // something to clear; fresh accounts with zero insights won't fire.
  useEffect(() => {
    if (loading) return;
    if (allClearCelebrated.current) return;
    if (!insights || insights.length === 0) return;
    const remaining = insights.filter((i) => !dismissedKinds.has(i.kind));
    if (remaining.length === 0) {
      allClearCelebrated.current = true;
      setShowAllClear(true);
      fireGoldSparkleBurst(0.35);
      toast.success("All clear, hero!", {
        description: "Every insight handled. Dash is proud.",
        duration: 4000,
      });
    }
  }, [insights, dismissedKinds, loading]);

  const refreshAfterChange = async () => {
    try {
      const [heroRes, bossRes, auditRes] = await Promise.all([
        api.get("/hero-score"),
        api.get("/boss-battle"),
        api.post("/audit/start").catch(() => ({ data: { insights: [] } })),
      ]);
      setHero(heroRes.data);
      setBoss(bossRes.data);
      const newInsights = auditRes.data?.insights || [];
      setInsights(newInsights);
      // If new actionable insights arrive, dismiss the all-clear card + arm
      // the celebration again for the next time hero clears the board.
      const actionable = newInsights.filter((i) => !dismissedKinds.has(i.kind));
      if (actionable.length > 0 && showAllClear) {
        setShowAllClear(false);
        allClearCelebrated.current = false;
      }
    } catch (e) { /* ignore */ }
  };

  const handleRescanFromAllClear = async () => {
    // Clear dismissals so the user can see every insight again post-rescan.
    try {
      window.localStorage.removeItem("mmh_dismissed_insight_kinds");
    } catch { /* ignore */ }
    setDismissedKinds(new Set());
    setShowAllClear(false);
    allClearCelebrated.current = false;
    try {
      const { data } = await api.post("/audit/start");
      setInsights(data?.insights || []);
      if (!data?.insights?.length) {
        toast.message("Shield scanned", { description: "No new drains detected. You're clean, hero." });
      } else {
        toast.success("Shield redeployed", { description: `${data.insights.length} insight${data.insights.length === 1 ? "" : "s"} surfaced.` });
      }
    } catch (e) {
      toast.error("Couldn't rescan. Try again.");
    }
  };

  const handleDismissInsight = (kind) => {
    setDismissedKinds((prev) => {
      const next = new Set(prev);
      next.add(kind);
      try {
        window.localStorage.setItem("mmh_dismissed_insight_kinds", JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  };

  const handleDefeat = async (id, original) => {
    // Client-side guard to give instant feedback (server enforces too).
    if (atDefeatLimit) {
      setPremiumMsg("Upgrade to Guardian to defeat more bills and keep raising your Hero Score!");
      setPremiumOpen(true);
      return;
    }
    setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, defeated: true } : s)));
    try {
      await api.post(`/subscriptions/${id}/defeat`);
      const [heroRes, bossRes, streakRes] = await Promise.all([
        api.get("/hero-score"),
        api.get("/boss-battle"),
        api.get("/streak"),
      ]);
      setHero(heroRes.data);
      setBoss(bossRes.data);
      setStreak(streakRes.data);
      // Open the Victory modal so the user can divert savings to the Vault.
      if (original?.monthly_cost > 0) {
        setVictorySub(original);
      } else {
        const isBoss = bossRes.data?.defeated_this_week && original?.id === id && original?.id === boss.sub?.id;
        toast.success(`Defeated ${original?.name || "the bill"}!`, {
          description: isBoss
            ? `Boss slain · 2× streak credit · $${(original?.monthly_cost || 0).toFixed(2)}/mo reclaimed`
            : `+${Math.max(1, heroRes.data.score - hero.score)} Hero Score · $${(original?.monthly_cost || 0).toFixed(2)}/mo reclaimed`,
        });
      }
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      // Revert optimistic defeated flag on error
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, defeated: false } : s)));
      if (e.response?.status === 402 && detail?.code === "DEFEAT_LIMIT_REACHED") {
        setPremiumMsg(detail.message || "Upgrade to Guardian to defeat more bills and keep raising your Hero Score!");
        setPremiumOpen(true);
        return;
      }
      toast.error("Battle failed. Try again.");
    }
  };

  const handleBossDefeat = async (id, sub) => {
    if (atDefeatLimit) {
      setPremiumMsg("Upgrade to Guardian to slay the weekly Boss and keep your streak alive!");
      setPremiumOpen(true);
      return;
    }
    setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, defeated: true } : s)));
    try {
      await api.post(`/subscriptions/${id}/defeat`);
      await refreshAfterChange();
      const streakRes = await api.get("/streak");
      setStreak(streakRes.data);
      // Open Victory modal for boss too.
      if (sub?.monthly_cost > 0) {
        setVictorySub(sub);
      } else {
        toast.success(`${sub.name} slain!`, {
          description: `Weekly Boss defeated · 2× streak credit · $${sub.monthly_cost.toFixed(2)}/mo reclaimed`,
          duration: 5000,
        });
      }
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, defeated: false } : s)));
      if (e.response?.status === 402 && detail?.code === "DEFEAT_LIMIT_REACHED") {
        setPremiumMsg(detail.message || "Upgrade to Guardian to slay the weekly Boss and keep your streak alive!");
        setPremiumOpen(true);
        return;
      }
      toast.error("Battle failed. Try again.");
    }
  };

  const handleCreated = (created) => {
    setSubs((prev) => [created, ...prev]);
    refreshAfterChange();
  };

  const handleSubChanged = (updated) => {
    setSubs((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    refreshAfterChange();
  };

  const handleSubDeleted = (id) => {
    setSubs((prev) => prev.filter((s) => s.id !== id));
    refreshAfterChange();
  };

  const handlePlaidLinked = async () => {
    try {
      const res = await api.get("/plaid/status");
      setPlaidStatus(res.data);
    } catch (e) { /* ignore */ }
  };

  const handlePlaidSynced = async (result) => {
    try {
      const [subsRes, heroRes, bossRes, plaidRes] = await Promise.all([
        api.get("/subscriptions"),
        api.get("/hero-score"),
        api.get("/boss-battle"),
        api.get("/plaid/status"),
      ]);
      setSubs(subsRes.data);
      setHero(heroRes.data);
      setBoss(bossRes.data);
      setPlaidStatus(plaidRes.data);
    } catch (e) { /* ignore */ }
  };

  const handlePlaidDisconnect = async () => {
    try {
      const [subsRes, heroRes, bossRes, plaidRes] = await Promise.all([
        api.get("/subscriptions"),
        api.get("/hero-score"),
        api.get("/boss-battle"),
        api.get("/plaid/status"),
      ]);
      setSubs(subsRes.data);
      setHero(heroRes.data);
      setBoss(bossRes.data);
      setPlaidStatus(plaidRes.data);
    } catch (e) { /* ignore */ }
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    try { window.localStorage.setItem("mmh_sort_by", value); } catch { /* ignore */ }
  };

  const handleHideDefeatedToggle = () => {
    setHideDefeated((prev) => {
      const next = !prev;
      try { window.localStorage.setItem("mmh_hide_defeated", String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const handleVictoryFund = async () => {
    if (!victorySub) return;
    setVictoryBusy(true);
    try {
      const { data } = await api.post("/vault/divert", {
        amount: Number(victorySub.monthly_cost || 0),
        source_sub_id: victorySub.id,
        note: `Defeated ${victorySub.name}`,
      });
      const delta = Number(victorySub.monthly_cost || 0);
      setVault(data);
      setVaultDelta(delta);
      toast.success(`+$${delta.toFixed(2)} deposited to your Hero Vault!`, {
        description: `Vault balance is now $${data.balance.toFixed(2)}.`,
        duration: 5000,
      });
      setVictorySub(null);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't fund Vault. Try again.");
    } finally {
      setVictoryBusy(false);
    }
  };

  const handleVictorySkip = () => {
    if (!victorySub) return;
    toast.success(`Defeated ${victorySub.name}!`, {
      description: `$${(victorySub.monthly_cost || 0).toFixed(2)}/mo reclaimed`,
      duration: 4000,
    });
    setVictorySub(null);
  };

  const displayedSubs = useMemo(() => {
    const filtered = hideDefeated ? subs.filter((s) => !s.defeated) : [...subs];
    const cmp = (a, b) => {
      if (sortBy === "alpha") {
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      }
      if (sortBy === "recent") {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      }
      // default: threat = highest monthly cost first
      return (b.monthly_cost || 0) - (a.monthly_cost || 0);
    };
    // Always render defeated subs at the bottom regardless of sort.
    filtered.sort((a, b) => {
      if (a.defeated !== b.defeated) return a.defeated ? 1 : -1;
      return cmp(a, b);
    });
    return filtered;
  }, [subs, hideDefeated, sortBy]);

  const defeatedCount = useMemo(() => subs.filter((s) => s.defeated).length, [subs]);

  const heroName = (user && user !== false && (user.name || user.email?.split("@")[0])) || "hero";
  const userTier = (user && user !== false && user.tier) || "recruit";
  const isPaid = userTier !== "recruit";

  return (
    <div className="hero-theme min-h-screen">
      <div className="max-w-md mx-auto w-full min-h-screen relative pb-40 pt-6 px-5" data-testid="dashboard-container">
        <header className="flex items-center justify-end gap-2 mb-4">
          <AppMenu />
          <button
            type="button"
            onClick={() => navigate("/upgrade")}
            className={`h-10 px-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.03] active:scale-[0.98] inline-flex items-center gap-1.5 shadow-md ${
              isPaid
                ? "bg-slate-900 text-amber-400 ring-2 ring-amber-400/30"
                : ""
            }`}
            style={
              !isPaid
                ? {
                    background: "linear-gradient(135deg, #FBBF24, #F5D849)",
                    color: "#0B1E1A",
                    fontFamily: "Outfit, sans-serif",
                    boxShadow: "0 8px 18px -4px rgba(245,216,73,0.5)",
                  }
                : { fontFamily: "Outfit, sans-serif" }
            }
            data-testid="header-upgrade-button"
            aria-label={isPaid ? `${userTier} plan — manage` : "Upgrade plan"}
          >
            {isPaid ? (
              <>
                <Crown className="w-3.5 h-3.5" strokeWidth={2.8} /> {userTier.toUpperCase()}
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2.8} /> Upgrade
              </>
            )}
          </button>
          <div className="w-10 h-10 rounded-2xl bg-[#11413B] text-[#F5D849] flex items-center justify-center ring-2 ring-[#F5D849]/25">
            <Trophy className="w-5 h-5" strokeWidth={2.4} />
          </div>
          <Button
            onClick={() => navigate("/account")}
            variant="ghost"
            className="h-10 w-10 p-0 rounded-2xl text-[#C7DAD3] hover:bg-[#11413B]/60"
            data-testid="account-link"
            aria-label="Account settings"
          >
            <User className="w-4 h-4" />
          </Button>
          <Button
            onClick={logout}
            variant="ghost"
            className="h-10 w-10 p-0 rounded-2xl text-[#94B0A6] hover:bg-[#11413B]/60"
            data-testid="logout-button"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </header>

        {/* Mascot spotlight — Dash, the Money Hero (compact) */}
        <section className="flex justify-center pt-1 pb-1">
          <HeroMascot size={180} />
        </section>

        <AIHeroPanel />

        {/* Welcome heading + streak pill */}
        <div className="mt-1 mb-4" data-testid="welcome-block">
          <p
            className="text-sm text-[#C7DAD3] font-semibold"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Welcome back,
          </p>
          <h1
            className="text-3xl sm:text-4xl font-black text-white leading-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="welcome-headline"
          >
            <span className="text-[#F5D849]">{firstName(heroName)}!</span>
          </h1>
          {streak.weeks > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5" data-testid="streak-pill">
              <Flame className="w-4 h-4 text-orange-400" strokeWidth={2.6} />
              <span
                className="text-[11px] font-black uppercase tracking-[0.16em] text-orange-400"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {streak.weeks * 7} Day Streak
              </span>
            </div>
          )}
        </div>

        {/* Command Center — 4 tiles */}
        <CommandCenterStrip
          heroLevel={heroLevel.level}
          levelLabel={heroLevel.label}
          vaultBalance={vault.balance || 0}
          billsDefeated={hero.defeated_count || 0}
          monthSaved={((hero.defeated_count || 0) > 0)
            ? subs.filter((s) => s.defeated).reduce((sum, s) => sum + Number(s.monthly_cost || 0), 0)
            : 0}
        />

        {/* Hero Score Shield | Boss Battle */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <HeroScoreShield
            score={heroLoaded ? hero.score : 0}
            size={170}
            onInfoClick={() => navigate("/analytics")}
          />
          {boss.sub ? (
            <div data-testid="boss-battle-section">
              <BossBattleCard
                sub={boss.sub}
                defeatedThisWeek={boss.defeated_this_week}
                onBossDefeat={handleBossDefeat}
              />
            </div>
          ) : (
            <div
              className="rounded-3xl bg-[#0B221D] border border-[#1B4238] p-5 flex flex-col items-center justify-center text-center min-h-[180px]"
              data-testid="boss-battle-empty"
            >
              <Swords className="w-8 h-8 text-[#5F7E76]" strokeWidth={2.4} />
              <p
                className="mt-2 text-[11px] font-black uppercase tracking-[0.18em] text-[#F5D849]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Next Boss Battle
              </p>
              <p className="mt-1 text-xs text-[#93B1A8] leading-snug">
                Connect your bank or add a subscription to unlock this week&rsquo;s boss.
              </p>
            </div>
          )}
        </div>

        {/* Daily Challenge | Recent Win */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="engagement-row">
          <DailyChallengeCard
            onNavigate={(route) => {
              if (route && route !== window.location.pathname) navigate(route);
            }}
          />
          <RecentWinCard subs={subs} />
        </div>

        {/* Action buttons — Defeat a Bill / Add New Account */}
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="dashboard-actions">
          <Button
            type="button"
            onClick={() => {
              const el = document.querySelector('[data-testid="monthly-battles-section"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="h-12 rounded-2xl bg-[#0E2924] hover:bg-[#13362E] text-[#F5D849] font-black uppercase tracking-wider text-[13px] ht-outline-gold"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="action-defeat-bill"
          >
            Defeat a Bill
          </Button>
          <AddSubscriptionDialog
            onCreated={handleCreated}
            onLimitReached={(detail) => {
              setPremiumMsg(detail?.message || "You've hit your subscription limit. Upgrade to track more battles.");
              setPremiumOpen(true);
            }}
          >
            <Button
              type="button"
              className="h-12 w-full rounded-2xl bg-[#0E2924] hover:bg-[#13362E] text-[#22E07A] font-black uppercase tracking-wider text-[13px] ht-outline-green"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="action-add-account"
            >
              Add New Account
            </Button>
          </AddSubscriptionDialog>
        </div>

        {/* Compact streak + at-risk row */}
        <div className="mt-4 flex items-center justify-center gap-2 text-xs" data-testid="streak-context-row">
          <Flame className={`w-3.5 h-3.5 ${streak.weeks > 0 ? "text-orange-400" : "text-[#5F7E76]"}`} />
          <span className="font-bold text-[#CDE0D9]" data-testid="streak-chip">
            {streak.weeks > 0
              ? `${streak.weeks}-week streak${streak.checked_in_this_week ? "" : " (check in!)"}`
              : "Start your weekly streak"}
            {streak.boss_weeks > 0 && (
              <span className="ml-1 text-[#F5D849]">· {streak.boss_weeks} boss{streak.boss_weeks === 1 ? "" : "es"} slain</span>
            )}
          </span>
          <span className="text-[#5F7E76]">·</span>
          <span className="font-semibold text-[#93B1A8]" data-testid="hero-score-context">
            ${hero.monthly_total.toFixed(2)}/mo at risk
          </span>
        </div>

        {isFree && (
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => navigate("/upgrade")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-all hover:scale-[1.03] ${
                atDefeatLimit
                  ? "bg-[#3A2D0F] text-[#F5D849] ring-2 ring-[#F5D849]/40"
                  : "bg-[#11413B] text-[#CDE0D9] hover:bg-[#13362E]"
              }`}
              data-testid="defeats-remaining-chip"
            >
              <Sparkles className="w-3 h-3" strokeWidth={3} />
              {atDefeatLimit
                ? "Free limit reached · upgrade for more"
                : `${defeatsRemaining}/${FREE_DEFEAT_LIMIT} free defeats left`}
            </button>
          </div>
        )}

        {/* Quick links */}
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="dashboard-quicklinks">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => navigate("/vault")}
            className="h-9 rounded-xl border-2 border-[#1B4238] bg-[#0E2924] text-[#CDE0D9] hover:border-[#F5D849]/50 hover:text-[#F5D849] font-extrabold gap-1.5 text-xs"
            data-testid="link-vault-page"
          >
            View Vault history
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => navigate("/analytics")}
            className="h-9 rounded-xl border-2 border-[#1B4238] bg-[#0E2924] text-[#CDE0D9] hover:border-[#22E07A]/50 hover:text-[#22E07A] font-extrabold gap-1.5 text-xs"
            data-testid="link-analytics-page"
          >
            Spending analytics
          </Button>
        </div>

        {/* Hero Insight Banner — surfaces the top actionable audit insight */}
        {showAllClear ? (
          <AllClearCard
            onRescan={handleRescanFromAllClear}
            streakDone={streak.checked_in_this_week}
          />
        ) : (
          <HeroInsightBanner
            insights={insights}
            subs={subs}
            onDefeat={handleDefeat}
            onDismiss={handleDismissInsight}
            dismissedKinds={dismissedKinds}
          />
        )}

        <NextBestAction
          refreshKey={subs?.length ?? 0}
          onDefeat={async (subId) => {
            const sub = (subs || []).find((s) => s.id === subId);
            if (sub) await handleDefeat(subId, sub);
          }}
          onOpenCoach={() => document.querySelector('[data-testid="coach-launcher"]')?.click()}
          onConnectBank={() => {
            // Scroll to and click the real Connect Bank button so users get
            // the exact same Plaid flow (including OAuth redirect + manual
            // fallback) as the top-of-Monthly-Battles button.
            const btn = document.querySelector('[data-testid="plaid-connect-button"]');
            if (btn) {
              btn.scrollIntoView({ behavior: "smooth", block: "center" });
              setTimeout(() => btn.click(), 400);
            }
          }}
        />

        {/* Monthly Battles */}
        <section className="mt-10" data-testid="monthly-battles-section">
          <div className="mb-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Section</p>
              <h2 className="text-2xl font-black text-slate-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                Monthly Battles
              </h2>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {plaidStatus.configured && (
                <ConnectBankButton
                  status={plaidStatus}
                  onLinked={handlePlaidLinked}
                  onSynced={handlePlaidSynced}
                  onDisconnect={handlePlaidDisconnect}
                />
              )}
              <AddSubscriptionDialog
                onCreated={handleCreated}
                onLimitReached={(detail) => {
                  setPremiumMsg(detail?.message || "You've hit your subscription limit. Upgrade to track more battles.");
                  setPremiumOpen(true);
                }}
              >
                <Button
                  size="sm"
                  className="h-9 rounded-xl bg-amber-400 hover:bg-amber-500 text-slate-900 font-extrabold gap-1.5 px-3"
                  data-testid="add-subscription-button"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.8} /> New
                </Button>
              </AddSubscriptionDialog>
            </div>

            {/* Sort + Filter controls */}
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="battles-controls">
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger
                  className="h-9 w-auto min-w-[160px] rounded-xl border-2 border-slate-200 bg-white text-xs font-extrabold text-slate-700 shadow-sm hover:border-slate-300 focus:ring-2 focus:ring-slate-900/10 px-3"
                  data-testid="sort-by-trigger"
                  aria-label="Sort battles"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <ArrowDownAZ className="w-4 h-4 text-slate-500" strokeWidth={2.6} />
                    <SelectValue />
                  </span>
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="threat" data-testid="sort-option-threat">
                    <span className="inline-flex items-center gap-2 font-bold">
                      <Zap className="w-3.5 h-3.5 text-red-500" strokeWidth={2.8} />
                      Highest Threat
                    </span>
                  </SelectItem>
                  <SelectItem value="recent" data-testid="sort-option-recent">
                    <span className="inline-flex items-center gap-2 font-bold">
                      <Clock className="w-3.5 h-3.5 text-sky-500" strokeWidth={2.8} />
                      Recently Discovered
                    </span>
                  </SelectItem>
                  <SelectItem value="alpha" data-testid="sort-option-alpha">
                    <span className="inline-flex items-center gap-2 font-bold">
                      <ArrowDownAZ className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2.8} />
                      Alphabetical
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleHideDefeatedToggle}
                disabled={defeatedCount === 0}
                className={`h-9 rounded-xl border-2 px-3 font-extrabold gap-1.5 transition-all ${
                  hideDefeated
                    ? "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-400"
                } disabled:opacity-50`}
                data-testid="toggle-hide-defeated"
                aria-pressed={!hideDefeated}
              >
                {hideDefeated ? (
                  <EyeOff className="w-4 h-4" strokeWidth={2.6} />
                ) : (
                  <Eye className="w-4 h-4" strokeWidth={2.6} />
                )}
                <span className="text-xs">
                  {hideDefeated
                    ? `Hide defeated${defeatedCount ? ` (${defeatedCount})` : ""}`
                    : `Showing defeated (${defeatedCount})`}
                </span>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-4" data-testid="battles-list">
            {loading && (
              <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 text-center text-slate-400 font-semibold">
                Loading battles…
              </div>
            )}

            {!loading && subs.length === 0 && (
              <div className="bg-white rounded-3xl border-2 border-dashed border-slate-200 p-8 text-center" data-testid="empty-state">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center">
                  <Swords className="w-7 h-7 text-amber-600" strokeWidth={2.4} />
                </div>
                <h3 className="mt-4 text-xl font-black text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Your battlefield is empty
                </h3>
                <p className="mt-1.5 text-sm font-medium text-slate-500 max-w-xs mx-auto">
                  Every bill and subscription is a battle. Add them here, then defeat the ones draining your gold.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2.5">
                  <AddSubscriptionDialog
                    onCreated={handleCreated}
                    onLimitReached={(detail) => {
                      setPremiumMsg(detail?.message || "You've hit your subscription limit. Upgrade to track more battles.");
                      setPremiumOpen(true);
                    }}
                  >
                    <Button
                      type="button"
                      className="h-11 w-full sm:w-auto rounded-2xl bg-amber-400 hover:bg-amber-500 text-slate-900 font-extrabold gap-2 px-5"
                      data-testid="empty-state-add-battle"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.8} />
                      Add your first battle
                    </Button>
                  </AddSubscriptionDialog>
                </div>
                {plaidStatus.configured && !plaidStatus.linked && (
                  <p className="mt-3 text-[11px] font-semibold text-slate-400">
                    Bank sync auto-discovers your recurring bills in seconds.
                  </p>
                )}
              </div>
            )}

            {!loading && subs.length > 0 && displayedSubs.length === 0 && (
              <div className="bg-white rounded-3xl border-2 border-dashed border-slate-200 p-6 text-center" data-testid="filtered-empty-state">
                <p className="text-sm font-bold text-slate-700">No active battles right now.</p>
                <p className="mt-1 text-xs font-medium text-slate-500">
                  Toggle &ldquo;Showing defeated&rdquo; to review your past wins.
                </p>
              </div>
            )}

            {!loading && displayedSubs.map((sub, i) => (
              <BattleCard
                key={sub.id}
                sub={sub}
                onDefeat={handleDefeat}
                onChanged={handleSubChanged}
                onDeleted={handleSubDeleted}
                index={i}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Fixed bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 pointer-events-none z-40">
        <div className="max-w-md mx-auto px-5 pb-5 pt-8 bg-gradient-to-t from-[#051611] via-[#051611]/95 to-transparent pointer-events-auto">
          <DeployShieldDialog>
            <Button
              className="w-full h-16 rounded-2xl bg-[#0E2924] hover:bg-[#13362E] active:scale-[0.99] text-[#22E07A] font-black tracking-tight transition-all duration-200 focus:ring-4 focus:ring-[#22E07A]/30 group px-3 ht-outline-green"
              data-testid="deploy-shield-button"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <Shield className="w-5 h-5 mr-2 text-[#22E07A] group-hover:rotate-[-8deg] transition-transform shrink-0" strokeWidth={2.6} />
              <span className="text-[14px] sm:text-[15px] whitespace-nowrap leading-tight uppercase tracking-wider">
                Deploy Shield <span className="text-[#F5D849] mx-0.5">·</span> <span className="text-[#F5D849]">Start Audit</span>
              </span>
            </Button>
          </DeployShieldDialog>
        </div>
      </div>

      <PremiumModal
        open={premiumOpen}
        onOpenChange={setPremiumOpen}
        message={premiumMsg}
      />

      <VictoryModal
        open={!!victorySub}
        onOpenChange={(next) => { if (!next) handleVictorySkip(); }}
        sub={victorySub}
        onFundVault={handleVictoryFund}
        onSkip={handleVictorySkip}
        busy={victoryBusy}
      />

      <InstallPrompt />
      <HeroCoach />
    </div>
  );
}
